import React, { useEffect, useRef } from "react";
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  tablePlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
  diffSourcePlugin,
  frontmatterPlugin,
  directivesPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  CodeToggle,
  CreateLink,
  InsertImage,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  BlockTypeSelect,
  Separator,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import "./mdxrichtexteditor.css";

const MDXRichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Enter description...",
  clsNames = "",
  styles = {},
  disabled = false,
}) => {
  const editorRef = useRef(null);

  // Track if editor has focus
  const [hasFocus, setHasFocus] = React.useState(false);

  // Only update when value changes from external source
  useEffect(() => {
    const currentValue = editorRef.current?.getMarkdown();
    if (editorRef.current && value !== currentValue && !hasFocus) {
      editorRef?.current?.setMarkdown(value);
    }
  }, [value, hasFocus]);

  const handleChange = (markdown) => {
    if (onChange) {
      onChange(markdown);
    }
  };

  const handleFocus = () => {
    setHasFocus(true);
  };

  const handleBlur = () => {
    setHasFocus(false);
  };

  return (
    <div className={`mdx-editor-container ${clsNames}`} style={styles}>
      <MDXEditor
        ref={editorRef}
        markdown={value || ""}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        readOnly={disabled}
        plugins={[
          // All default plugins for full functionality
          headingsPlugin(),
          listsPlugin(),
          quotePlugin(),
          thematicBreakPlugin(),
          markdownShortcutPlugin(),
          linkPlugin(),
          linkDialogPlugin(),
          imagePlugin({
            imageAutocompleteSuggestions: [],
            imageUploadHandler: async () =>
              Promise.resolve("https://via.placeholder.com/300x200"),
          }),
          tablePlugin(),
          codeBlockPlugin({ defaultCodeBlockLanguage: "javascript" }),
          codeMirrorPlugin({
            codeBlockLanguages: {
              js: "JavaScript",
              css: "CSS",
              txt: "Text",
              tsx: "TypeScript",
              jsx: "JSX",
              html: "HTML",
              json: "JSON",
              md: "Markdown",
            },
          }),
          diffSourcePlugin({ viewMode: "rich-text", diffMarkdown: "" }),
          frontmatterPlugin(),
          directivesPlugin(),
          // Default toolbar with all standard buttons
          toolbarPlugin({
            toolbarContents: () => (
              <>
                <UndoRedo />
                <Separator />
                <BoldItalicUnderlineToggles />
                <CodeToggle />
                <Separator />
                <ListsToggle />
                <Separator />
                <BlockTypeSelect />
                <Separator />
                <CreateLink />
                <InsertImage />
                <Separator />
                <InsertTable />
                <InsertThematicBreak />
              </>
            ),
          }),
        ]}
      />
    </div>
  );
};

export default MDXRichTextEditor;
